import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@/icons/Icon'
import { ViewModeToggle } from '@/lib/viewMode'
import { useAuth } from '@/lib/auth'
import { LogoMark } from '@/components/Logo'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { useNavState } from '@/lib/navState'

const PRIMARY_NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: 'layout-grid' },
  { to: '/alerts', label: 'Alerts', icon: 'bell' },
  { to: '/change-response', label: 'Change Response Board', icon: 'git-pull-request', title: 'Change Response Board' },
  { to: '/circular-feed', label: 'Circular Feed', icon: 'rss' },
  { to: '/briefings', label: 'Briefings', icon: 'file-text' },
  { to: '/analytics', label: 'Analytics', icon: 'bar-chart-2' },
] as const

const SETUP_NAV = [
  { to: '/alert-routing', label: 'Alert routing', icon: 'bell' },
  { to: '/carrier-profile', label: 'Carrier Profile', icon: 'sliders' },
  { to: '/data-sources', label: 'Data Sources', icon: 'refresh-cw' },
  { to: '/connections', label: 'Connections', icon: 'link' },
  { to: '/settings/profile', label: 'Settings', icon: 'settings' },
  { to: '/sync-history', label: 'Sync History', icon: 'clock' },
] as const

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? 'ix-nav-item is-active' : 'ix-nav-item'
}

/** The screens that hide something in Public view, and so offer the toggle. */
const VIEW_MODE_PAGES = ['/dashboard', '/circular-feed', '/carrier-profile']

export function AppLayout({ title, children }: { title: string; children: ReactNode }) {
  const { expanded, toggle, close } = useNavState()
  const { me, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const showViewMode = VIEW_MODE_PAGES.some((p) => pathname === p || pathname.startsWith(`${p}/`))

  useEffect(() => {
    document.title = `${title} · Insuronix`
  }, [title])

  const onSignOut = async (e: React.MouseEvent) => {
    e.preventDefault()
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="ix-app">
      <div className="ix-scrim" onClick={close} />

      <aside className="ix-sidebar">
        {/* The mark is the way home from anywhere in the app. */}
        <NavLink className="ix-sidebar__brand" to="/dashboard" aria-label="Insuronix dashboard">
          <LogoMark variant="sidebar" />
        </NavLink>

        <div className="ix-sidebar__scroll">
          <nav className="ix-sidebar__group">
            {PRIMARY_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass} title={'title' in item ? item.title : undefined}>
                <Icon name={item.icon} size={16} />
                <span className="ix-nav-item__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <nav className="ix-sidebar__group">
            <div className="ix-sidebar__group-label">Setup</div>
            {SETUP_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                <Icon name={item.icon} size={16} />
                <span className="ix-nav-item__label">{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="ix-sidebar__footer">
          <div className="ix-avatar">{me?.user.initials ?? '··'}</div>
          <div className="ix-sidebar__user">
            <div className="ix-sidebar__user-name">{me?.user.fullName ?? ''}</div>
            <div className="ix-sidebar__user-links">
              <NavLink to="/carrier-profile" style={{ color: 'var(--ix-primary)' }}>
                Profile
              </NavLink>
              <span>•</span>
              <NavLink to="/onboarding/source" style={{ color: 'var(--ix-primary)' }}>
                Replay
              </NavLink>
            </div>
          </div>
          <a className="ix-signout" href="/login" title="Sign out" aria-label="Sign out" onClick={onSignOut}>
            <Icon name="arrow-up-from-line" size={16} />
          </a>
        </div>
      </aside>

      <div className="ix-main">
        <header className="ix-topbar">
          <div className="ix-topbar__left">
            <button
              className="ix-topbar__menu"
              type="button"
              aria-label="Toggle navigation"
              aria-expanded={expanded}
              onClick={toggle}
              style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}
            >
              <Icon name="menu" size={18} />
            </button>
            <h1 className="ix-topbar__title">{title}</h1>
          </div>
          <div className="ix-topbar__right">
            {/* Only the three screens that actually hide something in Public
                view offer the toggle; elsewhere it changed nothing on the page. */}
            {showViewMode && <ViewModeToggle />}
            <NotificationBell />
            <GlobalSearch />
          </div>
        </header>
        <div className="ix-page">{children}</div>
      </div>
    </div>
  )
}

function GlobalSearch() {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`)
      }}
    >
      <label className="ix-search">
        <Icon name="search" size={16} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search circulars, briefings, filings..."
          aria-label="Search"
        />
      </label>
    </form>
  )
}

export default AppLayout
